import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Shield, Zap, Users, Check, X, Calculator } from 'lucide-react';
import { ReactivationCalculator } from '../components/ReactivationCalculator';
import { LandingSwitcher } from '../components/LandingSwitcher';
import './landing-b2b.css';

export const BusinessLandingPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  const [openFaq, setOpenFaq] = useState(null);

  const content = {
    es: {
      // HERO
      eyebrow: "ALIANZA DE PREEMINENCIA",
      heroTitle: <>Tu base de datos es una <span>mina de oro olvidada.</span><br/>Deja que nuestra IA extraiga el valor por ti.</>,
      heroSub: "Mientras otros asfixian tu margen, nosotros protegemos tu negocio. Reactiva a tus clientes por WhatsApp, automatiza sus cumpleaños y ofréceles premios de lujo (París o Canarias) sin que te cueste un solo euro de tu beneficio.",
      ctaPrimary: "Activar mi Auditoría de Reactivación Gratis",
      ctaSecondary: "Ser Comercio Fundador — Riesgo Cero",
      
      // Trust Card
      trustTitle: "Motor de Reactivación",
      trustSubtitle: "Tecnología que trabaja por ti",
      metrics: [
        { value: "×33", label: "ROI Potencial" },
        { value: "24h", label: "Tiempo de Activación" },
        { value: "0€", label: "Inversión Inicial" },
        { value: "100%", label: "Control Tuyo" }
      ],

      // COMPARISON
      compareEyebrow: "LA DIFERENCIA REAL",
      compareTitle: "No somos otra plataforma de descuentos",
      compareSub: "Ellos te quitan clientes y margen. Nosotros te damos infraestructura de fidelización.",
      compareRows: [
        { feature: "¿Quién recibe el pago del cliente?", them: "La plataforma (te pagan después)", us: "Tú, directamente" },
        { feature: "¿Quién controla el precio y condiciones?", them: "La plataforma impone reglas", us: "Tú decides todo" },
        { feature: "¿Tipo de cliente que llega?", them: "Caza-ofertas de una sola vez", us: "Vecinos que pueden volver" },
        { feature: "¿Qué pasa con tu margen?", them: "Se reduce drásticamente", us: "Tú lo proteges siempre" },
        { feature: "¿Fidelización incluida?", them: "No existe", us: "Sistema de puntos integrado" }
      ],
      compareColThem: "Plataformas de Descuento",
      compareColUs: "LokAlGrow",

      // AI SECTION
      aiEyebrow: "MOTOR DE REACTIVACIÓN IA",
      aiTitle: "Tu base de datos, finalmente activada",
      aiText: "Tienes clientes que no han vuelto en meses. Nuestra IA conversa con ellos por WhatsApp para llenar mesas o estantes vacíos en horas bajas. Sin presión, con personalización.",
      aiFeatures: [
        "Mensajes personalizados según historial de compra",
        "Conversación natural, no spam robótico",
        "Promociones solo cuando tú las activas",
        "Reportes de reactivación en tiempo real"
      ],
      waName: "LokAlGrow IA",
      waStatus: "Activo ahora",
      waMessages: [
        { type: "bot", text: "¡Hola María! 👋 Hace tiempo que no vienes por La Trattoria. Esta semana tenemos tu pasta favorita con un 20% especial para ti." },
        { type: "user", text: "¡Oh qué bien! ¿Hasta cuándo es válido?" },
        { type: "bot", text: "Hasta el viernes. ¿Te reservo mesa para 2 el jueves a las 21h como la última vez?" }
      ],

      // BIRTHDAY CLUB
      birthdayEyebrow: "CLUB VIP DE CUMPLEAÑOS",
      birthdayTitle: "Configura y Olvida",
      birthdaySub: "Sube tu lista de clientes con sus cumpleaños. Nuestra plataforma envía automáticamente felicitaciones y ofertas especiales durante 7 días. Tú solo ves llegar clientes felices.",
      birthdayHighlight: "\"Tú cubres el coste técnico del mensaje; nosotros solo ganamos si el cliente cruza tu puerta. Pago por éxito, no por promesas.\"",
      birthdaySteps: [
        { num: "01", title: "Sube tu lista", desc: "Excel o CSV con nombre, teléfono y fecha de cumpleaños" },
        { num: "02", title: "Configura tu oferta", desc: "Define el descuento o regalo de cumpleaños" },
        { num: "03", title: "Nosotros hacemos el resto", desc: "Campaña automática de 7 días antes y después" }
      ],

      // COMMITMENT
      commitmentEyebrow: "COMPROMISO DE SOCIO",
      commitmentTitle: "No somos un gasto más",
      commitmentText: <>Sabemos que el comercio local lucha contra impuestos y grandes cadenas. <strong>No somos un gasto más, somos tu infraestructura de fidelización.</strong> Ponemos nuestra tecnología y nuestros premios internacionales a tu servicio para que parezcas una multinacional ante tus clientes — sin el presupuesto de una multinacional.</>,

      // REWARDS
      rewardsEyebrow: "RESPALDO INTERNACIONAL",
      rewardsTitle: "Premios de lujo que tus clientes pueden ganar",
      rewardsSub: "Gracias a nuestra red, tus clientes acumulan puntos canjeables por experiencias que antes solo estaban al alcance de grandes corporaciones.",
      rewards: [
        { tag: "ESCAPADA PREMIUM", title: "Fin de Semana en París", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600" },
        { tag: "PARAÍSO CERCANO", title: "5 Noches en Canarias", img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600" },
        { tag: "EXPERIENCIA VIP", title: "Cena Gourmet para 2", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600" }
      ],

      // FAQ
      faqEyebrow: "PREGUNTAS FRECUENTES",
      faqTitle: "Respuestas directas",
      faqs: [
        { 
          q: "¿Y si mis clientes solo vienen por el descuento?",
          a: "El sistema de puntos los incentiva a volver repetidamente. No es un descuento de una vez — es el inicio de una relación de fidelización."
        },
        { 
          q: "¿Cuánto cuesta realmente?",
          a: "Empezar es gratis. Solo pagas por resultados: un pequeño porcentaje cuando el cliente efectivamente compra. Sin cuotas mensuales ni compromisos."
        },
        { 
          q: "¿Necesito conocimientos técnicos?",
          a: "Cero. Si sabes usar WhatsApp, sabes usar LokAlGrow. Configurar tu primera campaña toma menos de 10 minutos."
        },
        { 
          q: "¿Funciona para cualquier tipo de negocio?",
          a: "Si tienes clientes locales que pueden volver, LokAlGrow funciona. Restaurantes, peluquerías, tiendas, servicios... el modelo es universal."
        }
      ],

      // FINAL CTA
      finalTitle: <>El departamento de marketing<br/>que siempre quisiste,<br/><span>ahora a tu alcance.</span></>,
      finalSub: "Sin agencias. Sin presupuestos enormes. Sin esperar 6 meses para ver resultados.",
      finalCta1: "Activar Auditoría Gratis",
      finalCta2: "Hablar con un Asesor",
      finalNote: "Primeros resultados en menos de 72 horas · lokalgrow.com"
    },
    en: {
      // HERO
      eyebrow: "PREEMINENCE ALLIANCE",
      heroTitle: <>Your database is a <span>forgotten gold mine.</span><br/>Let our AI extract the value for you.</>,
      heroSub: "While others squeeze your margin, we protect your business. Reactivate customers via WhatsApp, automate their birthdays, and offer luxury prizes (Paris or Canary Islands) without costing you a single euro of profit.",
      ctaPrimary: "Activate My Free Reactivation Audit",
      ctaSecondary: "Become a Founding Business — Zero Risk",
      
      // Trust Card
      trustTitle: "Reactivation Engine",
      trustSubtitle: "Technology that works for you",
      metrics: [
        { value: "×33", label: "Potential ROI" },
        { value: "24h", label: "Activation Time" },
        { value: "€0", label: "Initial Investment" },
        { value: "100%", label: "Your Control" }
      ],

      // COMPARISON
      compareEyebrow: "THE REAL DIFFERENCE",
      compareTitle: "Not another discount platform",
      compareSub: "They take your customers and margin. We give you loyalty infrastructure.",
      compareRows: [
        { feature: "Who receives customer payment?", them: "The platform (they pay you later)", us: "You, directly" },
        { feature: "Who controls price and conditions?", them: "Platform imposes rules", us: "You decide everything" },
        { feature: "Type of customer you get?", them: "One-time deal hunters", us: "Neighbours who can return" },
        { feature: "What happens to your margin?", them: "Drastically reduced", us: "You always protect it" },
        { feature: "Loyalty included?", them: "Doesn't exist", us: "Integrated points system" }
      ],
      compareColThem: "Discount Platforms",
      compareColUs: "LokAlGrow",

      // AI SECTION
      aiEyebrow: "AI REACTIVATION ENGINE",
      aiTitle: "Your database, finally activated",
      aiText: "You have customers who haven't returned in months. Our AI chats with them via WhatsApp to fill empty tables or shelves during slow hours. No pressure, fully personalised.",
      aiFeatures: [
        "Personalised messages based on purchase history",
        "Natural conversation, not robotic spam",
        "Promotions only when you activate them",
        "Real-time reactivation reports"
      ],
      waName: "LokAlGrow AI",
      waStatus: "Active now",
      waMessages: [
        { type: "bot", text: "Hi Maria! 👋 It's been a while since you visited La Trattoria. This week we have your favourite pasta with a special 20% off just for you." },
        { type: "user", text: "Oh great! Until when is it valid?" },
        { type: "bot", text: "Until Friday. Shall I book a table for 2 on Thursday at 9pm like last time?" }
      ],

      // BIRTHDAY CLUB
      birthdayEyebrow: "VIP BIRTHDAY CLUB",
      birthdayTitle: "Set It and Forget It",
      birthdaySub: "Upload your customer list with their birthdays. Our platform automatically sends congratulations and special offers for 7 days. You just watch happy customers arrive.",
      birthdayHighlight: "\"You cover the technical cost of the message; we only earn if the customer walks through your door. Pay for success, not for promises.\"",
      birthdaySteps: [
        { num: "01", title: "Upload your list", desc: "Excel or CSV with name, phone and birthday" },
        { num: "02", title: "Set your offer", desc: "Define the birthday discount or gift" },
        { num: "03", title: "We do the rest", desc: "Automatic 7-day campaign before and after" }
      ],

      // COMMITMENT
      commitmentEyebrow: "PARTNER COMMITMENT",
      commitmentTitle: "We're not another expense",
      commitmentText: <>We know local businesses fight against taxes and big chains. <strong>We're not another expense, we're your loyalty infrastructure.</strong> We put our technology and international prizes at your service so you look like a multinational to your customers — without the multinational budget.</>,

      // REWARDS
      rewardsEyebrow: "INTERNATIONAL BACKING",
      rewardsTitle: "Luxury prizes your customers can win",
      rewardsSub: "Thanks to our network, your customers accumulate points redeemable for experiences that were previously only available to large corporations.",
      rewards: [
        { tag: "PREMIUM GETAWAY", title: "Weekend in Paris", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600" },
        { tag: "NEARBY PARADISE", title: "5 Nights in Canary Islands", img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600" },
        { tag: "VIP EXPERIENCE", title: "Gourmet Dinner for 2", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600" }
      ],

      // FAQ
      faqEyebrow: "FREQUENTLY ASKED QUESTIONS",
      faqTitle: "Direct answers",
      faqs: [
        { 
          q: "What if my customers only come for the discount?",
          a: "The points system incentivises them to return repeatedly. It's not a one-time discount — it's the start of a loyalty relationship."
        },
        { 
          q: "How much does it really cost?",
          a: "Starting is free. You only pay for results: a small percentage when the customer actually buys. No monthly fees or commitments."
        },
        { 
          q: "Do I need technical knowledge?",
          a: "Zero. If you know how to use WhatsApp, you know how to use LokAlGrow. Setting up your first campaign takes less than 10 minutes."
        },
        { 
          q: "Does it work for any type of business?",
          a: "If you have local customers who can return, LokAlGrow works. Restaurants, hair salons, shops, services... the model is universal."
        }
      ],

      // FINAL CTA
      finalTitle: <>The marketing department<br/>you always wanted,<br/><span>now within reach.</span></>,
      finalSub: "No agencies. No huge budgets. No waiting 6 months to see results.",
      finalCta1: "Activate Free Audit",
      finalCta2: "Talk to an Advisor",
      finalNote: "First results in less than 72 hours · lokalgrow.com"
    },
    fr: {
      // HERO
      eyebrow: "ALLIANCE DE PRÉEMINENCE",
      heroTitle: <>Votre base de données est une <span>mine d'or oubliée.</span><br/>Laissez notre IA en extraire la valeur pour vous.</>,
      heroSub: "Pendant que d'autres étouffent votre marge, nous protégeons votre commerce. Réactivez vos clients par WhatsApp, automatisez leurs anniversaires et offrez-leur des prix de luxe (Paris ou Canaries) sans que cela ne vous coûte un seul euro de profit.",
      ctaPrimary: "Activer mon Audit de Réactivation Gratuit",
      ctaSecondary: "Devenir Commerce Fondateur — Zéro Risque",
      
      // Trust Card
      trustTitle: "Moteur de Réactivation",
      trustSubtitle: "La technologie qui travaille pour vous",
      metrics: [
        { value: "×33", label: "ROI Potentiel" },
        { value: "24h", label: "Temps d'Activation" },
        { value: "0€", label: "Investissement Initial" },
        { value: "100%", label: "Votre Contrôle" }
      ],

      // COMPARISON
      compareEyebrow: "LA VRAIE DIFFÉRENCE",
      compareTitle: "Pas une autre plateforme de réductions",
      compareSub: "Ils prennent vos clients et votre marge. Nous vous donnons une infrastructure de fidélisation.",
      compareRows: [
        { feature: "Qui reçoit le paiement du client ?", them: "La plateforme (ils vous paient après)", us: "Vous, directement" },
        { feature: "Qui contrôle le prix et les conditions ?", them: "La plateforme impose ses règles", us: "Vous décidez de tout" },
        { feature: "Type de client que vous recevez ?", them: "Chasseurs d'offres ponctuels", us: "Voisins qui peuvent revenir" },
        { feature: "Qu'arrive-t-il à votre marge ?", them: "Réduite drastiquement", us: "Vous la protégez toujours" },
        { feature: "Fidélisation incluse ?", them: "N'existe pas", us: "Système de points intégré" }
      ],
      compareColThem: "Plateformes de Réduction",
      compareColUs: "LokAlGrow",

      // AI SECTION
      aiEyebrow: "MOTEUR DE RÉACTIVATION IA",
      aiTitle: "Votre base de données, enfin activée",
      aiText: "Vous avez des clients qui ne sont pas revenus depuis des mois. Notre IA discute avec eux par WhatsApp pour remplir les tables ou étagères vides aux heures creuses. Sans pression, entièrement personnalisé.",
      aiFeatures: [
        "Messages personnalisés selon l'historique d'achat",
        "Conversation naturelle, pas de spam robotique",
        "Promotions uniquement quand vous les activez",
        "Rapports de réactivation en temps réel"
      ],
      waName: "LokAlGrow IA",
      waStatus: "Actif maintenant",
      waMessages: [
        { type: "bot", text: "Bonjour Maria ! 👋 Cela fait longtemps que vous n'êtes pas venue à La Trattoria. Cette semaine, nous avons vos pâtes préférées avec -20% spécialement pour vous." },
        { type: "user", text: "Oh super ! Jusqu'à quand c'est valable ?" },
        { type: "bot", text: "Jusqu'à vendredi. Je vous réserve une table pour 2 jeudi à 21h comme la dernière fois ?" }
      ],

      // BIRTHDAY CLUB
      birthdayEyebrow: "CLUB VIP ANNIVERSAIRE",
      birthdayTitle: "Configurez et Oubliez",
      birthdaySub: "Téléchargez votre liste de clients avec leurs anniversaires. Notre plateforme envoie automatiquement des félicitations et des offres spéciales pendant 7 jours. Vous regardez simplement arriver des clients heureux.",
      birthdayHighlight: "\"Vous couvrez le coût technique du message ; nous ne gagnons que si le client passe votre porte. Paiement au succès, pas aux promesses.\"",
      birthdaySteps: [
        { num: "01", title: "Téléchargez votre liste", desc: "Excel ou CSV avec nom, téléphone et date d'anniversaire" },
        { num: "02", title: "Configurez votre offre", desc: "Définissez la réduction ou le cadeau d'anniversaire" },
        { num: "03", title: "Nous faisons le reste", desc: "Campagne automatique de 7 jours avant et après" }
      ],

      // COMMITMENT
      commitmentEyebrow: "ENGAGEMENT PARTENAIRE",
      commitmentTitle: "Nous ne sommes pas une dépense de plus",
      commitmentText: <>Nous savons que les commerces locaux luttent contre les taxes et les grandes chaînes. <strong>Nous ne sommes pas une dépense de plus, nous sommes votre infrastructure de fidélisation.</strong> Nous mettons notre technologie et nos prix internationaux à votre service pour que vous ressembliez à une multinationale aux yeux de vos clients — sans le budget d'une multinationale.</>,

      // REWARDS
      rewardsEyebrow: "SOUTIEN INTERNATIONAL",
      rewardsTitle: "Des prix de luxe que vos clients peuvent gagner",
      rewardsSub: "Grâce à notre réseau, vos clients accumulent des points échangeables contre des expériences qui n'étaient auparavant accessibles qu'aux grandes entreprises.",
      rewards: [
        { tag: "ESCAPADE PREMIUM", title: "Week-end à Paris", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600" },
        { tag: "PARADIS PROCHE", title: "5 Nuits aux Canaries", img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600" },
        { tag: "EXPÉRIENCE VIP", title: "Dîner Gourmet pour 2", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600" }
      ],

      // FAQ
      faqEyebrow: "QUESTIONS FRÉQUENTES",
      faqTitle: "Réponses directes",
      faqs: [
        { 
          q: "Et si mes clients ne viennent que pour la réduction ?",
          a: "Le système de points les incite à revenir régulièrement. Ce n'est pas une réduction ponctuelle — c'est le début d'une relation de fidélisation."
        },
        { 
          q: "Combien ça coûte vraiment ?",
          a: "Commencer est gratuit. Vous ne payez que pour les résultats : un petit pourcentage quand le client achète effectivement. Sans frais mensuels ni engagements."
        },
        { 
          q: "Ai-je besoin de connaissances techniques ?",
          a: "Zéro. Si vous savez utiliser WhatsApp, vous savez utiliser LokAlGrow. Configurer votre première campagne prend moins de 10 minutes."
        },
        { 
          q: "Ça fonctionne pour tout type de commerce ?",
          a: "Si vous avez des clients locaux qui peuvent revenir, LokAlGrow fonctionne. Restaurants, salons de coiffure, boutiques, services... le modèle est universel."
        }
      ],

      // FINAL CTA
      finalTitle: <>Le département marketing<br/>que vous avez toujours voulu,<br/><span>maintenant à votre portée.</span></>,
      finalSub: "Sans agences. Sans budgets énormes. Sans attendre 6 mois pour voir des résultats.",
      finalCta1: "Activer l'Audit Gratuit",
      finalCta2: "Parler à un Conseiller",
      finalNote: "Premiers résultats en moins de 72 heures · lokalgrow.com"
    }
  };

  // Determine content based on language
  const lang = i18n.language?.substring(0, 2) || 'es';
  const getContent = () => {
    if (lang === 'fr') return content.fr;
    if (lang === 'es' || isSpanish) return content.es;
    return content.en;
  };
  
  const c = getContent();

  return (
    <div className="b2b-landing">
      {/* Landing Switcher */}
      <LandingSwitcher />
      
      {/* HERO */}
      <section className="b2b-hero">
        <div className="b2b-hero-inner">
          <div className="b2b-hero-left">
            <motion.div 
              className="b2b-eyebrow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Shield className="b2b-eyebrow-icon" />
              {c.eyebrow}
            </motion.div>
            
            <motion.h1
              className="b2b-hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {c.heroTitle}
            </motion.h1>
            
            <motion.p 
              className="b2b-hero-sub"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {c.heroSub}
            </motion.p>
            
            <motion.div 
              className="b2b-hero-ctas"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/demo-reactivacion" className="b2b-btn-gold" data-testid="b2b-cta-primary">
                {c.ctaPrimary}
              </Link>
              <Link to="/register?type=business&founder=true" className="b2b-btn-outline" data-testid="b2b-cta-secondary">
                {c.ctaSecondary}
              </Link>
            </motion.div>
          </div>
          
          <motion.div 
            className="b2b-hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="b2b-trust-card">
              <div className="b2b-trust-header">
                <div className="b2b-trust-icon">
                  <Zap />
                </div>
                <div>
                  <div className="b2b-trust-title">{c.trustTitle}</div>
                  <div className="b2b-trust-subtitle">{c.trustSubtitle}</div>
                </div>
              </div>
              <div className="b2b-trust-metrics">
                {c.metrics.map((m, i) => (
                  <div key={i} className="b2b-metric">
                    <div className="b2b-metric-value">{m.value}</div>
                    <div className="b2b-metric-label">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="b2b-compare">
        <div className="b2b-compare-inner">
          <span className="b2b-section-eyebrow">{c.compareEyebrow}</span>
          <h2 className="b2b-section-title">{c.compareTitle}</h2>
          <p className="b2b-section-sub">{c.compareSub}</p>
          
          <div className="b2b-table-wrap">
            <table className="b2b-table">
              <thead>
                <tr>
                  <th></th>
                  <th className="b2b-col-bad">{c.compareColThem}</th>
                  <th className="b2b-col-good">{c.compareColUs}</th>
                </tr>
              </thead>
              <tbody>
                {c.compareRows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td><span className="b2b-cross"><X size={18} /></span> {row.them}</td>
                    <td><span className="b2b-check"><Check size={18} /></span> {row.us}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* AI REACTIVATION */}
      <section className="b2b-ai">
        <div className="b2b-ai-inner">
          <div className="b2b-ai-content">
            <span className="b2b-section-eyebrow" style={{ textAlign: 'left' }}>{c.aiEyebrow}</span>
            <h2>{c.aiTitle}</h2>
            <p>{c.aiText}</p>
            <div className="b2b-ai-features">
              {c.aiFeatures.map((f, i) => (
                <div key={i} className="b2b-ai-feature">
                  <div className="b2b-ai-feature-icon"><Check size={14} /></div>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="b2b-wa-mockup">
            <div className="b2b-wa-header">
              <div className="b2b-wa-avatar">🤖</div>
              <div>
                <div className="b2b-wa-name">{c.waName}</div>
                <div className="b2b-wa-status">{c.waStatus}</div>
              </div>
            </div>
            <div className="b2b-wa-messages">
              {c.waMessages.map((msg, i) => (
                <div key={i} className={`b2b-wa-msg b2b-wa-${msg.type}`}>
                  {msg.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BIRTHDAY CLUB */}
      <section className="b2b-birthday">
        <div className="b2b-birthday-inner">
          <span className="b2b-section-eyebrow" style={{ color: 'var(--b2b-gold)' }}>{c.birthdayEyebrow}</span>
          <h2>{c.birthdayTitle}</h2>
          <p className="b2b-birthday-sub">{c.birthdaySub}</p>
          
          <div className="b2b-birthday-highlight">
            <p>{c.birthdayHighlight}</p>
          </div>
          
          <div className="b2b-birthday-steps">
            {c.birthdaySteps.map((step, i) => (
              <div key={i} className="b2b-birthday-step">
                <div className="b2b-birthday-step-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="b2b-commitment">
        <div className="b2b-commitment-inner">
          <span className="b2b-section-eyebrow">{c.commitmentEyebrow}</span>
          <h2>{c.commitmentTitle}</h2>
          <p className="b2b-commitment-text">{c.commitmentText}</p>
        </div>
      </section>

      {/* REWARDS PREVIEW */}
      <section className="b2b-rewards">
        <div className="b2b-rewards-inner">
          <span className="b2b-section-eyebrow">{c.rewardsEyebrow}</span>
          <h2 className="b2b-section-title">{c.rewardsTitle}</h2>
          <p className="b2b-section-sub">{c.rewardsSub}</p>
          
          <div className="b2b-rewards-grid">
            {c.rewards.map((r, i) => (
              <div key={i} className="b2b-reward-card">
                <img src={r.img} alt={r.title} loading="lazy" />
                <div className="b2b-reward-overlay">
                  <span className="b2b-reward-tag">{r.tag}</span>
                  <div className="b2b-reward-title">{r.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="b2b-faq">
        <div className="b2b-faq-inner">
          <span className="b2b-section-eyebrow">{c.faqEyebrow}</span>
          <h2 className="b2b-section-title" style={{ marginBottom: '48px' }}>{c.faqTitle}</h2>
          
          {c.faqs.map((faq, i) => (
            <div key={i} className="b2b-faq-item">
              <button 
                className={`b2b-faq-q ${openFaq === i ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-${i}`}
              >
                {faq.q}
                <ChevronDown />
              </button>
              {openFaq === i && (
                <div className="b2b-faq-a">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="b2b-final">
        <div className="b2b-final-inner">
          <h2>{c.finalTitle}</h2>
          <p className="b2b-final-sub">{c.finalSub}</p>
          
          <div className="b2b-final-ctas">
            <Link to="/demo-reactivacion" className="b2b-btn-gold" data-testid="b2b-final-cta">
              {c.finalCta1}
            </Link>
            <a href="https://wa.me/34600000000" className="b2b-btn-outline" target="_blank" rel="noopener noreferrer">
              {c.finalCta2}
            </a>
          </div>
          
          <p className="b2b-final-note">{c.finalNote}</p>
        </div>
      </section>
    </div>
  );
};

export default BusinessLandingPage;
